
import { getAuthenticatedSupabase } from '../supabaseClient';
import { BudgetItem, Vendor, Ring, CeremonyContact } from '../types';
import { calculateBudgetSummary } from './budget-calculations';

/**
 * Aggregates the total budget for a wedding based on linked budget items.
 * This is the SINGLE SOURCE OF TRUTH for totals.
 */
export const aggregateBudget = async (weddingId: string) => {
  const supabase = getAuthenticatedSupabase();
  const { data } = await supabase.from('budget_items')
    .select('*')
    .eq('wedding_id', weddingId)
    .eq('include_in_budget', true);
  
  const items = (data || []) as BudgetItem[];
  
  const summary = calculateBudgetSummary(items);
  
  return {
    totalPlanned: summary.totalPlanned,
    totalPaid: summary.totalPaid,
    totalRemaining: summary.totalRemaining,
    items: summary.items
  };
};

/**
 * Whether a vendor should be included in the budget (all statuses except canceled).
 */
export const isVendorIncludedInBudget = (vendor: Vendor): boolean =>
  vendor.status !== 'canceled';

/**
 * Syncs a Vendor to the budget items table.
 */
export const syncVendorToBudget = async (vendor: Vendor) => {
  if (!vendor.id) return;
  const include = isVendorIncludedInBudget(vendor);
  
  // Logic: 
  // - Potential: planned = estimate, paid = 0 (unless specified)
  // - Booked: planned = estimate, paid = paid_amount
  const item: Partial<BudgetItem> = {
    wedding_id: vendor.wedding_id,
    source_type: 'vendor',
    source_id: vendor.id,
    category: vendor.type,
    title: vendor.name,
    planned_amount: vendor.price_estimate || 0,
    paid_amount: vendor.paid_amount || 0,
    include_in_budget: include
  };
  await upsertBudgetItem(item);
};

/**
 * Syncs a Ring to the budget items table.
 */
export const syncRingToBudget = async (ring: Ring) => {
  if (!ring.id) return;
  const include = ring.status !== 'canceled';
  const label = ring.owner === 'nevesta' ? 'Nevěsta' : 'Ženich';
  
  const item: Partial<BudgetItem> = {
    wedding_id: ring.wedding_id,
    source_type: 'ring',
    source_id: ring.id,
    category: 'Prstýnky',
    title: `Prstýnek – ${label} (${ring.style_name})`,
    planned_amount: ring.price_estimate || 0,
    paid_amount: ring.paid_amount || 0,
    include_in_budget: include
  };
  await upsertBudgetItem(item);
};

/**
 * Syncs a Ceremony Contact (fee) to the budget items table.
 */
export const syncCeremonyToBudget = async (contact: CeremonyContact) => {
  if (!contact.id) return;
  
  // Only sync if there is a price involved
  const hasPrice = (contact.price_estimate || 0) > 0 || (contact.paid_amount || 0) > 0;
  if (!hasPrice) return;

  const include = contact.status !== 'canceled';
  const item: Partial<BudgetItem> = {
    wedding_id: contact.wedding_id,
    source_type: 'ceremony',
    source_id: contact.id,
    category: 'Obřad',
    title: `${contact.role_type} – ${contact.name}`,
    planned_amount: contact.price_estimate || 0,
    paid_amount: contact.paid_amount || 0,
    include_in_budget: include
  };
  await upsertBudgetItem(item);
};

/**
 * Helper to upsert a budget item based on source_id + source_type
 */
const upsertBudgetItem = async (item: Partial<BudgetItem>) => {
  const supabase = getAuthenticatedSupabase();
  const { data } = await supabase.from('budget_items')
    .select('id')
    .eq('source_type', item.source_type)
    .eq('source_id', item.source_id)
    .maybeSingle();

  if (data) {
    await supabase.from('budget_items').update(item).eq('id', data.id);
  } else {
    if (item.include_in_budget) {
      await supabase.from('budget_items').insert(item);
    }
  }
};
